import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Info, Settings, Lock, User, Mail, ShieldAlert, Building, Check, Loader2, Eye, EyeOff } from 'lucide-react';

interface ProfileTabsProps {
  user: any;
  activeTab: 'overview' | 'edit' | 'security';
  setActiveTab: (tab: 'overview' | 'edit' | 'security') => void;
  name: string;
  setName: (val: string) => void;
  orgName: string;
  setOrgName: (val: string) => void;
  currentPassword: string;
  setCurrentPassword: (val: string) => void;
  password: string;
  setPassword: (val: string) => void;
  confirmPassword: string;
  setConfirmPassword: (val: string) => void;
  showCurrentPassword: boolean;
  setShowCurrentPassword: (val: boolean) => void;
  showNewPassword: boolean;
  setShowNewPassword: (val: boolean) => void;
  showConfirmPassword: boolean;
  setShowConfirmPassword: (val: boolean) => void;
  showPasswordErrors: boolean;
  isUpdating: boolean;
  isOrgAdmin: boolean;
  checks: {
    length: boolean;
    uppercase: boolean;
    lowercase: boolean;
    number: boolean;
    special: boolean;
  };
  handleUpdateInfo: (e: React.FormEvent) => void;
  handleUpdatePassword: (e: React.FormEvent) => void;
}

export function ProfileTabs({
  user,
  activeTab,
  setActiveTab,
  name,
  setName,
  orgName,
  setOrgName,
  currentPassword,
  setCurrentPassword,
  password,
  setPassword,
  confirmPassword,
  setConfirmPassword,
  showCurrentPassword,
  setShowCurrentPassword,
  showNewPassword,
  setShowNewPassword,
  showConfirmPassword,
  setShowConfirmPassword,
  showPasswordErrors,
  isUpdating,
  isOrgAdmin,
  checks,
  handleUpdateInfo,
  handleUpdatePassword
}: ProfileTabsProps) {
  const isSuperAdmin = user.role === 'SuperAdmin';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%', height: '460px', boxSizing: 'border-box' }}>
      {/* Navigation Tabs */}
      <div style={{
        display: 'flex',
        gap: '4px',
        borderBottom: '1px solid var(--admin-card-border)',
        paddingBottom: '1px',
        flexShrink: 0
      }}>
        <button
          type="button"
          onClick={() => setActiveTab('overview')}
          style={{
            padding: '8px 14px',
            border: 'none',
            background: 'none',
            fontSize: '12px',
            fontWeight: 700,
            fontFamily: "'Space Grotesk', sans-serif",
            color: activeTab === 'overview' ? 'var(--admin-card-text)' : 'var(--admin-card-subtext)',
            cursor: 'pointer',
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            borderRadius: '0'
          }}
        >
          <Info size={13} />
          Overview
          {activeTab === 'overview' && (
            <motion.div
              layoutId="profileTabIndicator"
              style={{
                position: 'absolute',
                bottom: '-1px',
                left: 0,
                right: 0,
                height: '2px',
                background: '#38bdf8'
              }}
            />
          )}
        </button>

        <button
          type="button"
          onClick={() => setActiveTab('edit')}
          style={{
            padding: '8px 14px',
            border: 'none',
            background: 'none',
            fontSize: '12px',
            fontWeight: 700,
            fontFamily: "'Space Grotesk', sans-serif",
            color: activeTab === 'edit' ? 'var(--admin-card-text)' : 'var(--admin-card-subtext)',
            cursor: 'pointer',
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            borderRadius: '0'
          }}
        >
          <Settings size={13} />
          Edit Profile
          {activeTab === 'edit' && (
            <motion.div
              layoutId="profileTabIndicator"
              style={{
                position: 'absolute',
                bottom: '-1px',
                left: 0,
                right: 0,
                height: '2px',
                background: '#38bdf8'
              }}
            />
          )}
        </button>

        <button
          type="button"
          onClick={() => setActiveTab('security')}
          style={{
            padding: '8px 14px',
            border: 'none',
            background: 'none',
            fontSize: '12px',
            fontWeight: 700,
            fontFamily: "'Space Grotesk', sans-serif",
            color: activeTab === 'security' ? 'var(--admin-card-text)' : 'var(--admin-card-subtext)',
            cursor: 'pointer',
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            borderRadius: '0'
          }}
        >
          <Lock size={13} />
          Security
          {activeTab === 'security' && (
            <motion.div
              layoutId="profileTabIndicator"
              style={{
                position: 'absolute',
                bottom: '-1px',
                left: 0,
                right: 0,
                height: '2px',
                background: '#38bdf8'
              }}
            />
          )}
        </button>
      </div>

      {/* Tab Panel Card */}
      <div style={{
        padding: '20px',
        borderRadius: '0',
        border: '1px solid var(--admin-card-border)',
        backgroundColor: 'var(--admin-card-bg)',
        flex: 1,
        width: '100%',
        boxSizing: 'border-box',
        overflowY: 'auto'
      }}>
        <AnimatePresence mode="wait">
          {activeTab === 'overview' && (
            <motion.div
              key="overview"
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 8 }}
              transition={{ duration: 0.15 }}
              style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
            >
              <div>
                <h3 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: '15px', fontWeight: 700, color: 'var(--admin-card-text)', margin: '0 0 4px 0' }}>
                  Profile Summary & Status
                </h3>
                <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '12px', color: 'var(--admin-card-subtext)', margin: 0 }}>
                  Snapshot of your personal profile and account credentials.
                </p>
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: '12px'
              }}>
                <div style={{ padding: '12px 14px', borderRadius: '0', backgroundColor: 'rgba(255, 255, 255, 0.02)', border: '1px solid var(--admin-card-border)' }}>
                  <span style={{ fontSize: '10px', fontWeight: 700, color: 'var(--admin-card-subtext)', textTransform: 'uppercase', display: 'block', marginBottom: '4px', letterSpacing: '0.05em' }}>
                    Display Name
                  </span>
                  <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--admin-card-text)', fontFamily: "'Inter', sans-serif" }}>
                    {user.name || 'N/A'}
                  </span>
                </div>

                <div style={{ padding: '12px 14px', borderRadius: '0', backgroundColor: 'rgba(255, 255, 255, 0.02)', border: '1px solid var(--admin-card-border)' }}>
                  <span style={{ fontSize: '10px', fontWeight: 700, color: 'var(--admin-card-subtext)', textTransform: 'uppercase', display: 'block', marginBottom: '4px', letterSpacing: '0.05em' }}>
                    Email Login
                  </span>
                  <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--admin-card-text)', fontFamily: "'Inter', sans-serif" }}>
                    {user.email || 'N/A'}
                  </span>
                </div>

                {isSuperAdmin ? (
                  <>
                    <div style={{ padding: '12px 14px', borderRadius: '0', backgroundColor: 'rgba(255, 255, 255, 0.02)', border: '1px solid var(--admin-card-border)' }}>
                      <span style={{ fontSize: '10px', fontWeight: 700, color: 'var(--admin-card-subtext)', textTransform: 'uppercase', display: 'block', marginBottom: '4px', letterSpacing: '0.05em' }}>
                        System Role
                      </span>
                      <span style={{ fontSize: '13px', fontWeight: 600, color: '#38bdf8', fontFamily: "'Inter', sans-serif" }}>
                        SuperAdmin
                      </span>
                    </div>

                    <div style={{ padding: '12px 14px', borderRadius: '0', backgroundColor: 'rgba(255, 255, 255, 0.02)', border: '1px solid var(--admin-card-border)' }}>
                      <span style={{ fontSize: '10px', fontWeight: 700, color: 'var(--admin-card-subtext)', textTransform: 'uppercase', display: 'block', marginBottom: '4px', letterSpacing: '0.05em' }}>
                        Account Status
                      </span>
                      <span style={{ fontSize: '13px', fontWeight: 600, color: '#10b981', fontFamily: "'Inter', sans-serif" }}>
                        Active SuperAdmin
                      </span>
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{ padding: '12px 14px', borderRadius: '0', backgroundColor: 'rgba(255, 255, 255, 0.02)', border: '1px solid var(--admin-card-border)' }}>
                      <span style={{ fontSize: '10px', fontWeight: 700, color: 'var(--admin-card-subtext)', textTransform: 'uppercase', display: 'block', marginBottom: '4px', letterSpacing: '0.05em' }}>
                        Organization Workspace
                      </span>
                      <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--admin-card-text)', fontFamily: "'Inter', sans-serif" }}>
                        {user.orgName || 'Workspace'}
                      </span>
                    </div>

                    <div style={{ padding: '12px 14px', borderRadius: '0', backgroundColor: 'rgba(255, 255, 255, 0.02)', border: '1px solid var(--admin-card-border)' }}>
                      <span style={{ fontSize: '10px', fontWeight: 700, color: 'var(--admin-card-subtext)', textTransform: 'uppercase', display: 'block', marginBottom: '4px', letterSpacing: '0.05em' }}>
                        Workspace Role
                      </span>
                      <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--admin-card-text)', fontFamily: "'Inter', sans-serif", textTransform: 'capitalize' }}>
                        {user.orgRole || 'Member'}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </motion.div>
          )}

          {activeTab === 'edit' && (
            <motion.div
              key="edit"
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 8 }}
              transition={{ duration: 0.15 }}
              style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
            >
              <div>
                <h3 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: '15px', fontWeight: 700, color: 'var(--admin-card-text)', margin: '0 0 4px 0' }}>
                  Edit Personal Details
                </h3>
                <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '12px', color: 'var(--admin-card-subtext)', margin: 0 }}>
                  Update your display name below.
                </p>
              </div>

              <form onSubmit={handleUpdateInfo} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <label style={{ fontSize: '11px', fontWeight: 700, color: 'var(--admin-card-subtext)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Full Name
                  </label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 10px 8px 34px',
                        borderRadius: '0',
                        border: '1px solid var(--admin-card-border)',
                        backgroundColor: 'rgba(255, 255, 255, 0.02)',
                        color: 'var(--admin-card-text)',
                        fontSize: '13px',
                        fontFamily: "'Inter', sans-serif",
                        boxSizing: 'border-box',
                        outline: 'none'
                      }}
                      placeholder="Enter full name"
                    />
                    <User size={14} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--admin-card-subtext)' }} />
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <label style={{ fontSize: '11px', fontWeight: 700, color: 'var(--admin-card-subtext)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Email Address
                    </label>
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '3px',
                      fontSize: '9px',
                      color: '#eab308',
                      backgroundColor: 'rgba(234, 179, 8, 0.08)',
                      padding: '2px 6px',
                      borderRadius: '0',
                      fontWeight: 700
                    }}>
                      <ShieldAlert size={10} /> Locked
                    </span>
                  </div>
                  <div style={{ position: 'relative' }}>
                    <input
                      type="email"
                      value={user.email || ''}
                      disabled
                      style={{
                        width: '100%',
                        padding: '8px 10px 8px 34px',
                        borderRadius: '0',
                        border: '1px solid var(--admin-card-border)',
                        backgroundColor: 'rgba(255, 255, 255, 0.01)',
                        color: 'var(--admin-card-subtext)',
                        fontSize: '13px',
                        fontFamily: "'Inter', sans-serif",
                        boxSizing: 'border-box',
                        cursor: 'not-allowed',
                        opacity: 0.7
                      }}
                    />
                    <Mail size={14} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--admin-card-subtext)' }} />
                  </div>
                </div>

                {!isSuperAdmin && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <label style={{ fontSize: '11px', fontWeight: 700, color: 'var(--admin-card-subtext)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Organization Name
                      </label>
                      {!isOrgAdmin && (
                        <span style={{
                          fontSize: '9px',
                          color: 'var(--admin-card-subtext)',
                          backgroundColor: 'rgba(255, 255, 255, 0.04)',
                          padding: '2px 6px',
                          borderRadius: '0',
                          fontWeight: 700
                        }}>
                          Read-Only
                        </span>
                      )}
                    </div>
                    <div style={{ position: 'relative' }}>
                      <input
                        type="text"
                        value={orgName}
                        disabled={!isOrgAdmin}
                        onChange={(e) => setOrgName(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '8px 10px 8px 34px',
                          borderRadius: '0',
                          border: '1px solid var(--admin-card-border)',
                          backgroundColor: isOrgAdmin ? 'rgba(255, 255, 255, 0.02)' : 'rgba(255, 255, 255, 0.01)',
                          color: isOrgAdmin ? 'var(--admin-card-text)' : 'var(--admin-card-subtext)',
                          fontSize: '13px',
                          fontFamily: "'Inter', sans-serif",
                          boxSizing: 'border-box',
                          cursor: isOrgAdmin ? 'text' : 'not-allowed',
                          opacity: isOrgAdmin ? 1 : 0.7,
                          outline: 'none'
                        }}
                        placeholder="Organization name"
                      />
                      <Building size={14} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--admin-card-subtext)' }} />
                    </div>
                  </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '6px' }}>
                  <button
                    type="submit"
                    disabled={isUpdating}
                    style={{
                      padding: '9px 18px',
                      borderRadius: '0',
                      backgroundColor: '#ffffff',
                      color: '#000000',
                      border: 'none',
                      fontSize: '12px',
                      fontWeight: 700,
                      fontFamily: "'Inter', sans-serif",
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    {isUpdating ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
                    Save Changes
                  </button>
                </div>
              </form>
            </motion.div>
          )}

          {activeTab === 'security' && (
            <motion.div
              key="security"
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 8 }}
              transition={{ duration: 0.15 }}
              style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
            >
              <div>
                <h3 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: '15px', fontWeight: 700, color: 'var(--admin-card-text)', margin: '0 0 4px 0' }}>
                  Update Security Password
                </h3>
                <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '12px', color: 'var(--admin-card-subtext)', margin: 0 }}>
                  Change your login credentials to protect your account.
                </p>
              </div>

              <form onSubmit={handleUpdatePassword} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <label style={{ fontSize: '11px', fontWeight: 700, color: 'var(--admin-card-subtext)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Current Password</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showCurrentPassword ? "text" : "password"}
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 36px 8px 34px',
                        borderRadius: '0',
                        border: '1px solid var(--admin-card-border)',
                        backgroundColor: 'rgba(255, 255, 255, 0.02)',
                        color: 'var(--admin-card-text)',
                        fontSize: '13px',
                        fontFamily: "'Inter', sans-serif",
                        boxSizing: 'border-box',
                        outline: 'none'
                      }}
                      placeholder="Verify current password"
                    />
                    <Lock size={14} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--admin-card-subtext)' }} />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      style={{
                        position: 'absolute',
                        right: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        color: 'var(--admin-card-subtext)',
                        cursor: 'pointer',
                        padding: 0,
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      {showCurrentPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                  {showPasswordErrors && !currentPassword && (
                    <p style={{ fontSize: '11px', color: '#ef4444', margin: '2px 0 0 0', display: 'flex', alignItems: 'center', gap: '4px', fontWeight: 500 }}>
                      <ShieldAlert size={12} /> Current password is required.
                    </p>
                  )}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <label style={{ fontSize: '11px', fontWeight: 700, color: 'var(--admin-card-subtext)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>New Password</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showNewPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 36px 8px 34px',
                        borderRadius: '0',
                        border: '1px solid var(--admin-card-border)',
                        backgroundColor: 'rgba(255, 255, 255, 0.02)',
                        color: 'var(--admin-card-text)',
                        fontSize: '13px',
                        fontFamily: "'Inter', sans-serif",
                        boxSizing: 'border-box',
                        outline: 'none'
                      }}
                      placeholder="Create new password"
                    />
                    <Lock size={14} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--admin-card-subtext)' }} />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      style={{
                        position: 'absolute',
                        right: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        color: 'var(--admin-card-subtext)',
                        cursor: 'pointer',
                        padding: 0,
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      {showNewPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                  {showPasswordErrors && !password && (
                    <p style={{ fontSize: '11px', color: '#ef4444', margin: '2px 0 0 0', display: 'flex', alignItems: 'center', gap: '4px', fontWeight: 500 }}>
                      <ShieldAlert size={12} /> New password is required.
                    </p>
                  )}
                  {showPasswordErrors && password && !/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{6,}$/.test(password) && (
                    <p style={{ fontSize: '11px', color: '#ef4444', margin: '2px 0 0 0', display: 'flex', alignItems: 'center', gap: '4px', fontWeight: 500 }}>
                      <ShieldAlert size={12} /> Password does not meet complexity rules.
                    </p>
                  )}
                  {password && (
                    <div style={{
                      marginTop: '4px',
                      padding: '10px 12px',
                      borderRadius: '0',
                      backgroundColor: 'rgba(255, 255, 255, 0.02)',
                      border: '1px solid var(--admin-card-border)',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '6px'
                    }}>
                      <span style={{ fontSize: '10px', fontWeight: 700, color: 'var(--admin-card-subtext)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Password Requirements:
                      </span>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '6px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: checks.length ? '#10b981' : 'var(--admin-card-subtext)' }}>
                          <div style={{ width: '10px', height: '10px', borderRadius: '0', border: '1px solid currentColor', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            {checks.length && <Check size={8} />}
                          </div>
                          Min 6 characters
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: checks.uppercase ? '#10b981' : 'var(--admin-card-subtext)' }}>
                          <div style={{ width: '10px', height: '10px', borderRadius: '0', border: '1px solid currentColor', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            {checks.uppercase && <Check size={8} />}
                          </div>
                          One uppercase
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: checks.lowercase ? '#10b981' : 'var(--admin-card-subtext)' }}>
                          <div style={{ width: '10px', height: '10px', borderRadius: '0', border: '1px solid currentColor', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            {checks.lowercase && <Check size={8} />}
                          </div>
                          One lowercase
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: checks.number ? '#10b981' : 'var(--admin-card-subtext)' }}>
                          <div style={{ width: '10px', height: '10px', borderRadius: '0', border: '1px solid currentColor', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            {checks.number && <Check size={8} />}
                          </div>
                          One number
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: checks.special ? '#10b981' : 'var(--admin-card-subtext)', gridColumn: 'span 2' }}>
                          <div style={{ width: '10px', height: '10px', borderRadius: '0', border: '1px solid currentColor', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            {checks.special && <Check size={8} />}
                          </div>
                          One special character (@$!%*?&)
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <label style={{ fontSize: '11px', fontWeight: 700, color: 'var(--admin-card-subtext)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Confirm New Password</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 36px 8px 34px',
                        borderRadius: '0',
                        border: '1px solid var(--admin-card-border)',
                        backgroundColor: 'rgba(255, 255, 255, 0.02)',
                        color: 'var(--admin-card-text)',
                        fontSize: '13px',
                        fontFamily: "'Inter', sans-serif",
                        boxSizing: 'border-box',
                        outline: 'none'
                      }}
                      placeholder="Confirm new password"
                    />
                    <Lock size={14} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--admin-card-subtext)' }} />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      style={{
                        position: 'absolute',
                        right: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        color: 'var(--admin-card-subtext)',
                        cursor: 'pointer',
                        padding: 0,
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      {showConfirmPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                  {showPasswordErrors && !confirmPassword && (
                    <p style={{ fontSize: '11px', color: '#ef4444', margin: '2px 0 0 0', display: 'flex', alignItems: 'center', gap: '4px', fontWeight: 500 }}>
                      <ShieldAlert size={12} /> Please confirm your new password.
                    </p>
                  )}
                  {(showPasswordErrors || (password && confirmPassword)) && password && confirmPassword && password !== confirmPassword && (
                    <p style={{ fontSize: '11px', color: '#ef4444', margin: '2px 0 0 0', display: 'flex', alignItems: 'center', gap: '4px', fontWeight: 500 }}>
                      <ShieldAlert size={12} /> Passwords do not match.
                    </p>
                  )}
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '6px' }}>
                  <button
                    type="submit"
                    disabled={isUpdating}
                    style={{
                      padding: '9px 18px',
                      borderRadius: '0',
                      backgroundColor: '#ffffff',
                      color: '#000000',
                      border: 'none',
                      fontSize: '12px',
                      fontWeight: 700,
                      fontFamily: "'Inter', sans-serif",
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    {isUpdating ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
                    Update Password
                  </button>
                </div>
              </form>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
